import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { SectionSidebarNav, type SectionSidebarNavButtonItem } from "@/components/SectionSidebarNav";
import { Spinner } from "@/components/ui/Spinner";
import { getUserLocalLogin, useCurrentUser, userHasGroup } from "@/lib/auth";
import { usePersonalNavigation } from "@/lib/personalNav";
import { EntryCapturePanel } from "@/pages/admin/zeiterfassung/EntryCapturePanel";
import { MonthOverviewPanel } from "@/pages/admin/zeiterfassung/MonthOverviewPanel";
import { YearOverviewPanel } from "@/pages/admin/zeiterfassung/YearOverviewPanel";
import { WorkTimeTablePanel } from "@/pages/admin/zeiterfassung/WorkTimeTablePanel";
import { AbsenceRequestPanel } from "@/pages/admin/zeiterfassung/AbsenceRequestPanel";
import { PersonalAccountSummary } from "@/pages/personal/PersonalAccountSummary";
import { OooSubstitutePanel } from "@/pages/personal/OooSubstitutePanel";
import { useRegisterPersonalSubNav } from "@/pages/personal/PersonalSubNavContext";
import "@/pages/admin/zeiterfassung/zeiterfassungMobile.css";

type PersonalTab = "capture" | "absence" | "month" | "year" | "worktimes";

const PERSONAL_TABS: ReadonlySet<string> = new Set<PersonalTab>([
  "capture",
  "absence",
  "month",
  "year",
  "worktimes",
]);

function parseTab(value: string | null): PersonalTab | null {
  return value && PERSONAL_TABS.has(value) ? (value as PersonalTab) : null;
}

export function MeinePersonalPage() {
  const { user, loading: userLoading } = useCurrentUser();
  const { backLink: personalBack } = usePersonalNavigation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<PersonalTab>(
    () => parseTab(searchParams.get("tab")) ?? "capture",
  );
  const [refreshKey, setRefreshKey] = useState(0);

  // Deep-Link `?tab=absence` (z. B. von der Personal-Startseite) übernehmen
  // und den Parameter danach aus der URL entfernen.
  useEffect(() => {
    const requested = parseTab(searchParams.get("tab"));
    if (!requested) return;
    setActiveTab(requested);
    const next = new URLSearchParams(searchParams);
    next.delete("tab");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const defaultEmployee = useMemo(() => {
    const login = getUserLocalLogin(user);
    const display = user?.name || user?.displayName || login;
    return { login, display };
  }, [user]);

  const bumpRefresh = () => setRefreshKey((k) => k + 1);

  const navItems = useMemo(
    (): SectionSidebarNavButtonItem[] => [
      {
        key: "capture",
        label: "Erfassen",
        active: activeTab === "capture",
        onClick: () => setActiveTab("capture"),
      },
      {
        key: "absence",
        label: "Abwesenheit",
        tourTarget: "abwesenheit-tab",
        active: activeTab === "absence",
        onClick: () => setActiveTab("absence"),
      },
      {
        key: "month",
        label: "Monat",
        active: activeTab === "month",
        onClick: () => setActiveTab("month"),
      },
      {
        key: "year",
        label: "Jahr",
        active: activeTab === "year",
        onClick: () => setActiveTab("year"),
      },
      {
        key: "worktimes",
        label: "Arbeitszeiten",
        active: activeTab === "worktimes",
        onClick: () => setActiveTab("worktimes"),
      },
    ],
    [activeTab],
  );
  const { nestInParentNav } = useRegisterPersonalSubNav("mein-bereich", navItems);

  if (userLoading) {
    return (
      <AppShell title="Mein Bereich" subtitle="Lade Profil…">
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell
      title="Mein Bereich"
      className="zeiterfassung-mobile-shell"
      subtitle="Arbeitszeiten erfassen, Abwesenheiten beantragen und Kalender einsehen."
      back={personalBack ?? undefined}
      actions={
        <>
          <Link
            to="/personal/me/einstellungen"
            className="inline-flex items-center gap-1.5 rounded-full border border-separator bg-card px-3 py-1.5 text-xs font-medium text-label-secondary transition hover:bg-fill-tertiary hover:text-label"
          >
            <SettingsIcon />
            Einstellungen
          </Link>
          {defaultEmployee.login ? (
            <PersonalAccountSummary
              employeeLogin={defaultEmployee.login}
              refreshKey={refreshKey}
            />
          ) : null}
        </>
      }
    >
      {nestInParentNav ? (
        <PanelContent
          activeTab={activeTab}
          defaultEmployee={defaultEmployee}
          refreshKey={refreshKey}
          onRefresh={bumpRefresh}
        />
      ) : (
        <div className="eh-bestellwesen-page-shell">
          <SectionSidebarNav items={navItems} ariaLabel="Mein Bereich" />
          <div className="eh-project-page-main min-w-0">
            <PanelContent
              activeTab={activeTab}
              defaultEmployee={defaultEmployee}
              refreshKey={refreshKey}
              onRefresh={bumpRefresh}
            />
          </div>
        </div>
      )}
    </AppShell>
  );
}

function PanelContent({
  activeTab,
  defaultEmployee,
  refreshKey,
  onRefresh,
}: {
  activeTab: PersonalTab;
  defaultEmployee: { login: string; display: string };
  refreshKey: number;
  onRefresh: () => void;
}) {
  const { user } = useCurrentUser();
  return (
    <>
      {activeTab === "capture" && (
        <EntryCapturePanel
          defaultEmployee={defaultEmployee}
          onCreated={onRefresh}
          allowEmployeePicker={false}
        />
      )}
      {activeTab === "absence" && (
        <>
          {userHasGroup(user, "Nachunternehmer") ? null : <OooSubstitutePanel />}
          <AbsenceRequestPanel
            refreshKey={refreshKey}
            defaultEmployeeLogin={defaultEmployee.login}
            defaultEmployeeDisplay={defaultEmployee.display}
            allowEmployeePicker={false}
            includeHeroOverlay
            onChanged={onRefresh}
          />
        </>
      )}
      {activeTab === "month" && (
        <MonthOverviewPanel
          refreshKey={refreshKey}
          defaultEmployeeLogin={defaultEmployee.login}
          defaultEmployeeDisplay={defaultEmployee.display}
          allowEmployeePicker={false}
          includeHeroOverlay
        />
      )}
      {activeTab === "year" && (
        <YearOverviewPanel
          refreshKey={refreshKey}
          defaultEmployeeLogin={defaultEmployee.login}
          defaultEmployeeDisplay={defaultEmployee.display}
          allowEmployeePicker={false}
          includeHeroOverlay
        />
      )}
      {activeTab === "worktimes" && (
        <WorkTimeTablePanel
          refreshKey={refreshKey}
          defaultEmployeeLogin={defaultEmployee.login}
          defaultEmployeeDisplay={defaultEmployee.display}
          allowEmployeePicker={false}
          includeHeroOverlay
        />
      )}
    </>
  );
}

function SettingsIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4" aria-hidden>
      <path
        d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9c.26.604.852.997 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
